import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Plus, FileText, Search, Receipt, ArrowRight, Calendar } from "lucide-react";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import ScheduleJobDialog from "@/components/ScheduleJobDialog";
import CustomerCommsButtons from "@/components/CustomerCommsButtons";

const EST_STATUS = {
  draft: "bg-zinc-100 text-zinc-800 border-zinc-200",
  sent: "bg-blue-100 text-blue-800 border-blue-200",
  viewed: "bg-amber-100 text-amber-800 border-amber-200",
  accepted: "bg-emerald-100 text-emerald-800 border-emerald-200",
  declined: "bg-rose-100 text-rose-800 border-rose-200",
  expired: "bg-zinc-200 text-zinc-700 border-zinc-300",
};

export default function Estimates() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const nav = useNavigate();

  const load = async () => {
    const { data } = await api.get("/estimates");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const [scheduleFor, setScheduleFor] = useState(null); // estimate object or null

  const convertToInvoice = async (e, estimate) => {
    e.stopPropagation();
    if (estimate.converted_to_invoice_id) {
      nav(`/app/invoices/${estimate.converted_to_invoice_id}`);
      return;
    }
    // Second-chance guard against double-clicks that beat the loading state
    if (!window.confirm(`Create an invoice from ${estimate.estimate_number}? The estimate stays as-is for your records.`)) return;
    try {
      const { data: inv } = await api.post(`/estimates/${estimate.id}/create-invoice`);
      toast.success(`Invoice ${inv.invoice_number} created`);
      // Update the row in-place so the button flips to "View invoice"
      setItems(prev => prev.map(x => x.id === estimate.id ? { ...x, converted_to_invoice_id: inv.id, converted_at: inv.created_at } : x));
      nav(`/app/invoices/${inv.id}`);
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail || err?.message || "";
      toast.error(`Convert failed${status ? ` (${status})` : ""}: ${detail}`);
    }
  };

  const filtered = items.filter((e) => {
    if (status !== "all" && e.status !== status) return false;
    if (!q) return true;
    const t = q.toLowerCase();
    return (e.title||"").toLowerCase().includes(t) || (e.estimate_number||"").toLowerCase().includes(t) || (e.customer_name||"").toLowerCase().includes(t);
  });

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="text-xs font-mono-accent uppercase tracking-widest text-[#FF5A00] mb-1">Sales</div>
          <h1 className="font-heading text-4xl font-bold">Estimates</h1>
        </div>
        <Button onClick={()=>nav("/app/estimates/new")} className="bg-[#FF5A00] hover:bg-[#E04F00] text-white btn-lift" data-testid="estimates-new-btn">
          <Plus className="w-4 h-4 mr-1"/> New estimate
        </Button>
      </div>

      <div className="bg-white border border-zinc-200 rounded-xl card-crisp overflow-hidden">
        <div className="p-4 border-b border-zinc-100 flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400"/>
            <Input value={q} onChange={(e)=>setQ(e.target.value)} placeholder="Search title, number, customer" className="pl-9" data-testid="estimates-search-input"/>
          </div>
          <div className="flex gap-1">
            {["all","draft","sent","viewed","accepted","declined"].map((s)=>(
              <button key={s} onClick={()=>setStatus(s)} className={`text-xs px-3 py-1.5 rounded-md border ${status===s?"bg-zinc-900 text-white border-zinc-900":"bg-white text-zinc-700 border-zinc-200 hover:border-zinc-300"}`} data-testid={`estimates-filter-${s}`}>{s}</button>
            ))}
          </div>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 text-xs uppercase text-zinc-500">
            <tr>
              <th className="text-left px-4 py-3">Number</th>
              <th className="text-left px-4 py-3">Title</th>
              <th className="text-left px-4 py-3">Customer</th>
              <th className="text-left px-4 py-3">Total</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-left px-4 py-3">Created</th>
              <th className="text-right px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((e) => (
              <tr key={e.id} onClick={()=>nav(`/app/estimates/${e.id}`)} className="border-t border-zinc-100 hover:bg-zinc-50 cursor-pointer" data-testid={`estimate-row-${e.id}`}>
                <td className="px-4 py-3 font-mono-accent text-zinc-700">{e.estimate_number}</td>
                <td className="px-4 py-3 font-medium">{e.title || "—"}</td>
                <td className="px-4 py-3 text-zinc-700">
                  <div>{e.customer_name || "—"}</div>
                  {e.customer_id && (
                    <div className="mt-1">
                      <CustomerCommsButtons
                        customer={{
                          id: e.customer_id,
                          phone: e.customer_phone,
                          email: e.customer_email,
                          sms_opted_out: e.customer_sms_opted_out,
                        }}
                        estimateId={e.id}
                        rowTestId={`estimate-row-${e.id}`}
                      />
                    </div>
                  )}
                </td>
                <td className="px-4 py-3 font-mono-accent">${(e.total||0).toLocaleString()}</td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-1 text-xs border rounded-md capitalize ${EST_STATUS[e.status] || "bg-zinc-100"}`}>{e.status}</span>
                  {e.converted_to_invoice_id && (
                    <span className="ml-2 inline-flex items-center gap-1 px-2 py-1 text-xs border rounded-md bg-emerald-50 text-emerald-800 border-emerald-200" title="This estimate has already been converted to an invoice">
                      <Receipt className="w-3 h-3"/> Converted
                    </span>
                  )}
                </td>
                <td className="px-4 py-3 text-zinc-500 text-xs">{e.created_at ? new Date(e.created_at).toLocaleDateString() : ""}</td>
                <td className="px-4 py-3 text-right">
                  <div className="inline-flex items-center gap-1.5">
                    {e.scheduled_job_id ? (
                      <Button size="sm" variant="outline" onClick={(ev)=>{ ev.stopPropagation(); nav(`/app/jobs/${e.scheduled_job_id}`); }} data-testid={`estimate-view-job-${e.id}`} title={e.scheduled_job_at ? `Scheduled for ${new Date(e.scheduled_job_at).toLocaleString()}` : "View scheduled job"}>
                        <Calendar className="w-3.5 h-3.5 mr-1"/> Job scheduled
                      </Button>
                    ) : e.status !== "declined" && (
                      <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white" onClick={(ev)=>{ ev.stopPropagation(); setScheduleFor(e); }} data-testid={`estimate-schedule-job-${e.id}`}>
                        <Calendar className="w-3.5 h-3.5 mr-1"/> Schedule Job
                      </Button>
                    )}
                    {e.converted_to_invoice_id ? (
                      <Button size="sm" variant="outline" onClick={(ev)=>{ ev.stopPropagation(); nav(`/app/invoices/${e.converted_to_invoice_id}`); }} data-testid={`estimate-view-invoice-${e.id}`}>
                        <ArrowRight className="w-3.5 h-3.5 mr-1"/> View invoice
                      </Button>
                    ) : (
                      <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={(ev)=>convertToInvoice(ev, e)} data-testid={`estimate-convert-invoice-${e.id}`}>
                        <Receipt className="w-3.5 h-3.5 mr-1"/> Convert to invoice
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan="7" className="text-center text-zinc-500 py-12">
                <FileText className="w-8 h-8 text-zinc-300 mx-auto mb-2"/>
                No estimates match your filters.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
      <ScheduleJobDialog
        open={!!scheduleFor}
        onClose={()=>setScheduleFor(null)}
        estimate={scheduleFor}
        onScheduled={(job)=>{
          setItems(prev => prev.map(x => x.id === scheduleFor.id ? { ...x, scheduled_job_id: job.id, scheduled_job_at: job.scheduled_start } : x));
          setScheduleFor(null);
          nav(`/app/jobs/${job.id}`);
        }}
      />
    </div>
  );
}
